import { supabase } from "./supabase";
import * as db from "./db";
import { erEkteProduksjon } from "./session";
import type { LogEntry, Session, StoredAnchor } from "./types";

/**
 * Loggen opp til basen.
 *
 * ÉN VEI, MED ÉTT UNNTAK SIDEN 29.10.2026.
 *
 * Appen sender. Den tar ikke imot — bortsett fra postene TEAMET DITT har
 * skrevet i oekta du selv staar i. Se `hentFraTeamet` nederst.
 *
 * Alt annet staar: ingen andre skriver til `log_entry` — redaktoerens
 * kommentarer og markeringer ligger i egne tabeller — og en post rettes
 * eller slettes bare der den ble skrevet, av den som skrev den. Det som
 * endret seg er hvem LOGGEN tilhoerer, ikke hvem POSTEN tilhoerer, og
 * derfor finnes det fortsatt ingen konflikt aa loese: ingen rad har to
 * skrivere.
 *
 * LOKALT FOERST, ALLTID. Posten staar paa skjermen i det den skrives, uten
 * dekning og uten konto. Opplastingen er en bakgrunnsoppgave som tar koen
 * naar nettet er der, og rekkefoelgen i koen er FOERST ANKER, SAA OEKT, SAA
 * POST — fremmednoeklene i Postgres taaler ikke annet.
 *
 * `upsert` paa id, ikke insert. Id-ene er klientgenererte nettopp for at det
 * skal gaa an: en post som ble sendt, men der svaret aldri kom fram, sendes
 * igjen og lander paa den samme raden. Den som kommer dobbelt opp skal ikke
 * bli to poster i redaktoerens spalte.
 *
 * PRIVAT GAAR INGEN VEI. Se `iKoe` i db.ts.
 *
 * Teksten ligger KRYPTERT lokalt og i klartekst her. Det er skjemaets eget
 * valg — `log_entry.text` i Postgres er `text` — og lagringen er i EU. Se
 * state/krypto.ts for hva den lokale krypteringen beskytter mot.
 */

/* Nok til at en travel hurtiglogg kommer fram i ett jafs, lite nok til at en
   daarlig linje ikke henger paa én stor foresporsel. */
const BOLK = 50;

/**
 * Det som ikke gikk opp sier fra.
 *
 * Samme moenster som `skriv` i SessionProvider: en lagring som feiler i
 * stillhet er det verste utfallet — posten staar paa skjermen, og ingen vet
 * at den ikke kom fram. Her gjelder det redaktoeren, som ville sett en
 * spalte som bare stanset.
 */
function klag(hva: string, feil: unknown): void {
  // eslint-disable-next-line no-console
  console.error(`[infoflyt] loggsynk: ${hva}`, feil);
  const kode = typeof feil === "object" && feil !== null && "code" in feil
    ? String((feil as { code: unknown }).code) : "";
  const sagt = typeof feil === "object" && feil !== null && "message" in feil
    ? String((feil as { message: unknown }).message) : String(feil);
  sisteKode = kode;
  sisteKlage = `${hva}${kode === "" ? "" : ` ${kode}`}: ${sagt} · bygd ${__BYGG__}`;
}

/**
 * Siste avvisning, i klartekst.
 *
 * `console.error` finnes ikke paa en telefon. En loggpost som ikke gaar opp
 * endte derfor som ingenting: posten staar paa skjermen, redaktoeren har den
 * ikke, og ingen av dem vet det. Menyen viser denne. Lagt til 25.10.2026,
 * etter at nettopp det skjedde.
 */
let sisteKlage = "";

export function klagen(): string {
  return sisteKlage;
}

/**
 * Serveren har SVART, og svaret er nei.
 *
 * «Venter paa nett» er en loegn naar raden er avvist av en policy: den
 * venter ikke paa noe, den kommer aldri fram slik den er. Brukeren
 * 02.11.2026 sto med «2 loggrader venter paa nett» og en 42501 under —
 * to paastander som ikke kan vaere sanne samtidig.
 *
 * `42501` er manglende rettighet (RLS), `23514` en sjekk som ikke gaar
 * (en laast produksjon). Begge staar til noen endrer noe ANNET enn
 * raden. `23503` staar utenfor: en manglende fremmednoekkel er som
 * regel en rad som ikke har rukket opp ennaa, og den loeser seg selv.
 */
const AVVIST = new Set(["42501", "23514"]);

let sisteKode = "";

export function avvist(): boolean {
  return AVVIST.has(sisteKode);
}

/** Lag-id slaas opp paa navn, én gang per oekt. Appen kjenner bare navnet. */
const lagIder = new Map<string, string | null>();

async function lagId(produksjonId: string, navn: string | null): Promise<string | null> {
  if (navn === null || navn === "") return null;
  const noekkel = `${produksjonId}:${navn}`;
  const kjent = lagIder.get(noekkel);
  if (kjent !== undefined) return kjent;
  if (supabase === null) return null;
  const { data } = await supabase
    .from("unit")
    .select("id")
    .eq("production_id", produksjonId)
    .eq("name", navn)
    .limit(1)
    .maybeSingle();
  const id = data === null ? null : String(data.id);
  lagIder.set(noekkel, id);
  return id;
}

async function sendAnker(oekt: Session, id: string): Promise<boolean> {
  const a = await db.anchorById(id);
  if (a === null) return true; // borte lokalt. Da er det ingenting aa sende.
  if (supabase === null) return false;
  const { error } = await supabase.from("tc_anchor").upsert({
    id: a.id,
    production_id: a.productionId,
    unit_id: await lagId(a.productionId, oekt.unitName),
    author_id: oekt.member.id,
    shoot_day: a.shootDay,
    tc_frames: a.tcFrames,
    framerate: a.framerate,
    drop_frame: a.dropFrame,
    monotonic_ms: a.monotonicMs,
    boot_id: a.bootId,
    wall_utc: new Date(a.wallUtcMs).toISOString(),
    source: a.source,
    /* `jam` er den de andre enhetene adopterer; `klokke` gjelder bare denne
       telefonen; `avledet` er vaar egen adopsjon. `derived_from` sier hvem
       opphavet var. Se state/tidskodesynk.ts og migrasjon 20261027091000. */
    kind: a.kind,
    derived_from: a.derivedFrom,
  });
  if (error !== null) klag("anker", error);
  return error === null;
}

async function sendOekt(oekt: Session, id: string): Promise<boolean> {
  const s = await db.sessionById(id);
  if (s === null) return true;
  if (supabase === null) return false;
  /*
   * Oekten maa ha et anker. Lokalt har den ikke noe — ankeret hoerer til
   * (produksjon, opptaksdag), slik at to logger paa samme dag deler det. I
   * Postgres er `anchor_id` paakrevd, saa det slaas opp her.
   *
   * Finnes det ikke ennaa, er oekten for tidlig ute: den ble aapnet foer
   * tidskoden ble satt. Da blir den liggende i koen til ankeret finnes.
   */
  const anker = await db.anchorForDay(s.productionId, s.shootDay);
  if (anker === null) return false;

  /*
   * ========  EN TEAMOEKT OVERSKRIVES ALDRI  ========
   *
   * Utloeseren paa tjeneren setter regiens navn paa oekta — men bare ved
   * INNSETTING. En rett `upsert` fra fotografens telefon ville oppdatert
   * raden og satt fotografens navn paa teamets logg, og redaktoeren ville
   * lett etter «Kari» i en spalte som het «Tom».
   *
   * Derfor: lag den om den ikke finnes, og roer den ellers ikke. Lukkingen
   * gaar for seg selv under, og den endrer bare status.
   */
  const paaLag = oekt.unitId !== "";
  const rad = {
    id: s.id,
    production_id: s.productionId,
    /* Teamets id naar vi har den. `lagId` slaar opp paa NAVN og lager
       raden om den mangler — den veien staar igjen for den som ikke har
       synket medlemskapet sitt ennaa. */
    unit_id: oekt.unitId !== "" ? oekt.unitId : await lagId(s.productionId, oekt.unitName),
    author_id: oekt.member.id,
    anchor_id: anker.id,
    shoot_day: s.shootDay,
    status: s.status,
    opened_at: new Date(s.openedAtUtcMs).toISOString(),
    closed_at: s.closedAtUtcMs === null ? null : new Date(s.closedAtUtcMs).toISOString(),
  };

  const { error } = await supabase
    .from("log_session")
    .upsert(rad, paaLag ? { ignoreDuplicates: true } : {});
  if (error !== null) { klag("oekt", error); return false; }

  /* Lukkingen er en egen setning, og den roerer bare status. Hvem som
     lukket dagen endrer ikke hvem loggen tilhoerer. */
  if (paaLag && s.status === "closed") {
    const { error: lukkefeil } = await supabase
      .from("log_session")
      .update({ status: "closed", closed_at: rad.closed_at })
      .eq("id", s.id);
    if (lukkefeil !== null) { klag("oekt lukkes", lukkefeil); return false; }
  }
  return true;
}

/**
 * Haken og svaret opp.
 *
 * MAA GAA ETTER POSTEN. `su_answer.entry_id` peker paa `log_entry`, og koen
 * er sortert slik at posten gaar foerst — se `loggkoen` i db.ts.
 *
 * Og den gaar OPP, som alt annet herfra. Haken har vaert bare lokal fram til
 * 24.10.2026: redaktoeren saa den bare gjennom rapporten, og svaret fanns
 * ikke. Naa leser reporteren og redaktoeren det samme.
 */
async function sendSvar(oekt: Session, id: string): Promise<boolean> {
  const v = await db.svarById(id);
  if (v === null) return true; // borte lokalt. Da er det ingenting aa sende.
  if (supabase === null) return false;
  const { error } = await supabase.from("su_answer").upsert({
    id: v.id,
    production_id: oekt.production.id,
    entry_id: v.entryId,
    question_id: v.spoersmaalId,
    participant_id: v.deltakerId,
    at_utc: new Date(v.atUtcMs).toISOString(),
    answer: v.svar === "" ? null : v.svar,
    answer_at: v.svarAtMs === null ? null : new Date(v.svarAtMs).toISOString(),
  });
  if (error !== null) klag("svar", error);
  return error === null;
}

/** Oekter vi alt har bedt koen om paa nytt. Hindrer at de kaller i ring. */
const bedtOm = new Set<string>();

async function sendPost(oekt: Session, id: string): Promise<boolean> {
  const e = await db.entryById(id);
  if (e === null) return true;
  if (supabase === null) return false;
  const { error } = await supabase.from("log_entry").upsert({
    id: e.id,
    session_id: e.sessionId,
    production_id: e.productionId,
    anchor_id: e.anchorId,
    author_id: oekt.member.id,
    /*
     * `button_id` sendes IKKE.
     *
     * Hurtigknappene er per bruker og ligger bare lokalt; en fremmednoekkel
     * til en rad som ikke finnes ville avvist hele posten. Etiketten er
     * uansett det redaktoeren trenger, og den staar paa posten — den ble
     * fanget i trykkoeyeblikket og skal ikke endres om knappen doepes om.
     */
    label: e.label,
    color: e.color,
    text: e.text,
    monotonic_ms: e.monotonicMs,
    boot_id: e.bootId,
    wall_utc: new Date(e.wallUtcMs).toISOString(),
    tc_state: e.tcState,
    participant_id: e.participantId,
    /*
     * HVOR POSTEN BLE TIL. Se migrasjon 20261102210000.
     *
     * Nettflata kan fylle ut en post den selv skrev, og den maa kunne se
     * forskjellen: originalen paa en app-post ligger HER, og en retting
     * gjort paa nett ville blitt overskrevet av nettopp denne `upsert`-en
     * ved neste synk.
     */
    origin: "app",
    deleted_at: e.deletedAt === null ? null : new Date(e.deletedAt).toISOString(),
  });
  if (error === null) return true;
  klag("post", error);

  /*
   * BER OM OEKTEN, VENTER IKKE PAA DEN.
   *
   * 23503 paa `session_id` betyr at oekta ikke finnes i basen. Koen kvitterer
   * ÉN gang, saa om oekta alt er kvittert — svaret kom aldri fram, eller
   * raden ble ryddet bort paa tjeneren — finnes det ingen som noen gang
   * sender den igjen. Da staar hver eneste post i den oekta fast for alltid,
   * og regi ser ingenting annet enn at redaktoeren ikke har loggen.
   *
   * Samme grep som rapportkoen fikk 22.10.2026, og det er samme feil: noe
   * ventet paa en rad ingen kom til aa sende. Én gang per oekt, ellers ville
   * nudgen kalt hit igjen i ring.
   */
  if (error.code === "23503" && !bedtOm.has(e.sessionId)) {
    bedtOm.add(e.sessionId);
    await db.koeOektPaaNytt(e.sessionId);
    window.dispatchEvent(new CustomEvent("infoflyt:loggkoe"));
  }
  return false;
}

/**
 * Toemmer koen. Returnerer hvor mange rader som gikk opp.
 *
 * HOPPER OVER DET SOM HENGER, OG TOEMMER RESTEN. Koen er sortert slik at
 * ankere gaar foer oekter, oekter foer poster og poster foer svar — og gaar en
 * oekt ikke opp, blir postene i den staaende. Men bare de: en annen oekts
 * poster skal fram uansett. Aa fortsette ville gitt en haandfull avviste
 * foresporsler per runde, og de ville kommet igjen hvert minutt.
 */
export async function send(oekt: Session): Promise<number> {
  if (supabase === null || !navigator.onLine) return 0;
  const produksjonId = oekt.production.id;
  if (produksjonId === "" || produksjonId === "privat") return 0;

  await db.openDb(produksjonId);
  const koe = (await db.loggkoen(produksjonId)).slice(0, BOLK);
  let sendt = 0;

  /*
   * Det som ikke gaar NAA. Resten av koen skal likevel toemmes.
   *
   * HER LAA FEILEN, og den er den samme som rapportkoen hadde 22.10.2026:
   * loekka stoppet ved den foerste raden som ikke gikk. Én rad som ALDRI kom
   * til aa gaa — en oekt uten anker, en rad avvist av skjemaet — blokkerte
   * dermed hver loggpost bak seg, for alltid og i stillhet. Regi skrev, posten
   * sto paa skjermen, og redaktoeren fikk ingenting.
   *
   * REKKEFOELGEN BETYR BARE NOE INNENFOR ÉN KJEDE: anker foer oekt, oekt foer
   * post, post foer svar. Gaar en oekt ikke opp, har postene i den ingen rad
   * aa henge paa — men en ANNEN oekts poster er upaavirket, og de skal fram.
   */
  const bloktAnker = new Set<string>();
  const bloktOekt = new Set<string>();
  const bloktPost = new Set<string>();

  for (const rad of koe) {
    if (rad.tabell === "tc_anchor") {
      if (await sendAnker(oekt, rad.id)) {
        await db.loggkoeKvitter(rad.tabell, rad.id);
        sendt += 1;
      } else {
        bloktAnker.add(rad.id);
      }
      continue;
    }

    if (rad.tabell === "log_session") {
      const s = await db.sessionById(rad.id);
      const anker = s === null
        ? null : await db.anchorForDay(s.productionId, s.shootDay);
      /* Ankeret maa opp foerst. Er det blokkert, er oekta det ogsaa. */
      if (anker !== null && bloktAnker.has(anker.id)) { bloktOekt.add(rad.id); continue; }
      if (await sendOekt(oekt, rad.id)) {
        await db.loggkoeKvitter(rad.tabell, rad.id);
        sendt += 1;
      } else {
        bloktOekt.add(rad.id);
      }
      continue;
    }

    if (rad.tabell === "su_answer") {
      const v = await db.svarById(rad.id);
      if (v !== null && bloktPost.has(v.entryId)) continue;
      if (await sendSvar(oekt, rad.id)) {
        await db.loggkoeKvitter(rad.tabell, rad.id);
        sendt += 1;
      }
      continue;
    }

    const e = await db.entryById(rad.id);
    if (e !== null && bloktOekt.has(e.sessionId)) { bloktPost.add(rad.id); continue; }
    if (await sendPost(oekt, rad.id)) {
      await db.loggkoeKvitter(rad.tabell, rad.id);
      sendt += 1;
    } else {
      bloktPost.add(rad.id);
    }
  }
  return sendt;
}

/** Hvor mange rader som venter. Flata viser det naar noe henger igjen. */
export async function ventende(produksjonId: string): Promise<number> {
  if (produksjonId === "" || produksjonId === "privat") return 0;
  await db.openDb(produksjonId);
  return (await db.loggkoen(produksjonId)).length;
}

/* =====================================================================
 *  HENTING: de andre paa teamet
 *
 *  Brukeren 29.10.2026: «Er de paa nett, saa skal appen hente loggposter
 *  fra de andre enhetene om det blir skrevet, hvis de ikke er paa nett
 *  skal det synkes opp foer rapporten skrives.»
 *
 *  Koen har gaatt én vei siden appen ble skrevet: opp. Dette er den
 *  foerste veien ned, og den er smal med vilje — den henter POSTER I MIN
 *  EGEN OEKT som noen andre har skrevet, og ingenting annet.
 *
 *  ========  ANKRENE MAA MED  ========
 *
 *  En post peker paa ankeret den ble logget mot, og det ankeret staar paa
 *  DEN ANDRES telefon. Uten det kan ikke tidskoden regnes ut her: vi
 *  havner paa veggklokka og flagger hver eneste av deres poster som
 *  `usikker`.
 *
 *  Med ankeret er regningen EKSAKT, fordi posten og ankeret kommer fra
 *  samme enhet og samme oppstart — `boot_id` stemmer, og den monotone
 *  tellingen er ubrutt. Det er noeyaktig det nettflata gjoer.
 * ===================================================================== */

/** Et anker vi har hentet ned skal ikke sendes opp igjen. */
async function hentAnkerNed(ankerId: string, produksjonId: string): Promise<void> {
  if (supabase === null) return;
  if (await db.anchorById(ankerId) !== null) return;

  const { data, error } = await supabase
    .from("tc_anchor")
    .select("id, shoot_day, tc_frames, framerate, drop_frame, monotonic_ms,"
      + " boot_id, wall_utc, source, kind, derived_from")
    .eq("id", ankerId)
    .maybeSingle();
  if (error !== null || data === null) return;

  const r = data as unknown as Record<string, unknown>;
  await db.lagreFremmedAnker({
    id: String(r["id"]),
    productionId: produksjonId,
    shootDay: String(r["shoot_day"]),
    /* Serveren baerer ingen egen kolonne for dette: paa en JAM er
       `wall_utc` jammens tid, og det er svaret. Et klokkeanker er ikke
       jammet i det hele tatt. Et avledet anker fra en annen enhet vet vi
       ikke jammens tid for herfra — da er adopsjonstiden det naermeste,
       og dette ankeret styrer uansett ikke vaar egen avlesning. */
    jammetUtcMs: String(r["kind"]) === "klokke"
      ? null : Date.parse(String(r["wall_utc"])),
    tcFrames: Number(r["tc_frames"]),
    framerate: String(r["framerate"]) as StoredAnchor["framerate"],
    dropFrame: Boolean(r["drop_frame"]),
    monotonicMs: Number(r["monotonic_ms"]),
    bootId: String(r["boot_id"]),
    wallUtcMs: Date.parse(String(r["wall_utc"])),
    source: "manual",
    kind: String(r["kind"]) as StoredAnchor["kind"],
    derivedFrom: r["derived_from"] == null ? null : String(r["derived_from"]),
  });
}

/**
 * Henter ned alt som staar i oekta paa serveren og ikke paa telefonen.
 *
 * Returnerer antallet som kom ned. Null naar det ikke er noe aa hente.
 *
 * ========  OGSAA DET MAN SELV SKREV PAA NETTFLATA  ========
 *
 * Her sto `.neq("author_id", oekt.member.id)` — «de ANDRE paa teamet» — og
 * det var for smalt. Brukeren 03.11.2026: «Hvis jeg som regi i team 1
 * skriver i liveloggen paa nettflata, og deretter skriver i loggflata i
 * appen, saa maa loggen fra nettflata ligge i loggen i app-flata, og
 * omvendt.» Mine egne poster fra nettflata baerer MIN forfatter-id, saa de
 * ble aldri hentet ned — og motsatt vei virket det, fordi nettflata leser
 * hele oekta.
 *
 * Det er samme oekt: `oektIdFor(produksjon, dag, team)` regnes likt begge
 * steder, og uten team finner nettflata oekta paa forfatter. Én logg per
 * dag, og én rapport.
 *
 * UTEN TEAM OGSAA, av samme grunn. Vakta paa `unitId` sto her fordi
 * funksjonen het «fra teamet»; en regissoer uten team skriver like fullt
 * begge steder.
 *
 * ========  UTBOKSEN VINNER  ========
 *
 * En rad som ligger i utboksen er skrevet eller rettet HER og har ikke
 * naadd opp. Da er den lokale kopien fasiten — ellers ville en henting
 * lagt serverens gamle tekst over en rettelse regi nettopp gjorde uten
 * dekning, og utboksen sendt den gamle opp igjen.
 *
 * KREVER NETT, OG DET ER GREIT. Dette er ikke logging: man kan logge i en
 * kjeller som foer, og resten kommer naar linja gjoer det.
 */
export async function hentFraTeamet(
  oekt: Session, oektId: string,
): Promise<number> {
  if (supabase === null || !erEkteProduksjon(oekt.production.id)) return 0;
  if (!navigator.onLine) return 0;

  const { data, error } = await supabase
    .from("log_entry")
    .select("id, anchor_id, author_id, label, color, text, monotonic_ms,"
      + " boot_id, wall_utc, tc_state, deleted_at, participant_id,"
      + " forfatter:production_member!log_entry_author_id_fkey(initials)")
    .eq("session_id", oektId);
  if (error !== null) { klag("henting", error); return 0; }
  if (data === null) return 0;

  let antall = 0;
  for (const rad of data as unknown as Record<string, unknown>[]) {
    if (await db.iUtboks(String(rad["id"]))) continue;
    const ankerId = String(rad["anchor_id"]);
    await hentAnkerNed(ankerId, oekt.production.id);

    const hvem = (rad["forfatter"] ?? {}) as { initials?: string };
    const endret = await db.lagreFremmedPost({
      id: String(rad["id"]),
      productionId: oekt.production.id,
      sessionId: oektId,
      anchorId: ankerId,
      monotonicMs: Number(rad["monotonic_ms"]),
      bootId: String(rad["boot_id"]),
      wallUtcMs: Date.parse(String(rad["wall_utc"])),
      buttonId: null,
      participantId: rad["participant_id"] == null
        ? null : String(rad["participant_id"]),
      label: String(rad["label"] ?? ""),
      color: rad["color"] == null ? null : String(rad["color"]),
      text: String(rad["text"] ?? ""),
      tcState: String(rad["tc_state"]) as LogEntry["tcState"],
      deletedAt: rad["deleted_at"] == null
        ? null : Date.parse(String(rad["deleted_at"])),
      /*
       * Forkortelsen er det som gjoer posten til en som ble skrevet ET
       * ANNET STED: den kan ikke rettes her, og den gaar ikke i utboksen.
       * Finnes den ikke, settes en strek — null ville gjort posten til
       * vaar egen.
       *
       * OGSAA PAA MINE EGNE fra nettflata. Originalen ligger der, og en
       * retting her ville gaatt opp som en `upsert` og overskrevet den.
       * Se CLAUDE.md § «Posten rettes der den ble skrevet».
       */
      skrevetAv: hvem.initials ?? "—",
    });
    /* Bare det som faktisk endret seg. Tallet vekker flata, og en vekking
       hvert fjerde sekund ville tegnet loggen paa nytt uten grunn. */
    if (endret) antall += 1;
  }
  return antall;
}
